import { useState, useEffect, useRef } from 'react';
import { platformAPI } from '../lib/api';
import { Activity, ShieldAlert, Zap, Globe, Users, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

function AnimatedCounter({ value, duration = 1800 }) {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    const start = prevRef.current;
    const end = value;
    if (start === end) return;
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) requestAnimationFrame(step);
      else prevRef.current = end;
    };
    requestAnimationFrame(step);
  }, [value, duration]);

  return <span>{display.toLocaleString()}</span>;
}

export function LiveThreatDashboard() {
  const [stats, setStats] = useState(null);
  const [pulse, setPulse] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await platformAPI.liveStats();
        setStats(res.data);
      } catch {}
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    const pulseInterval = setInterval(() => setPulse(p => !p), 1500);
    return () => { clearInterval(interval); clearInterval(pulseInterval); };
  }, []);

  if (!stats) return null;

  const items = [
    { icon: Activity, label: 'SCANS TODAY', value: stats.scans_today, color: '#FF7A3D' },
    { icon: ShieldAlert, label: 'THREATS BLOCKED', value: stats.threats_detected, color: '#ff003c' },
    { icon: Zap, label: 'AVG RESPONSE', value: `${(stats.avg_response_ms / 1000).toFixed(1)}s`, raw: true, color: '#00E5C1' },
    { icon: Globe, label: 'COUNTRIES', value: stats.countries_protected, color: '#ffbd2e' },
    { icon: Users, label: 'OPERATIVES', value: stats.total_users, color: '#FF7A3D' },
    { icon: TrendingUp, label: 'UPTIME', value: `${stats.uptime_pct}%`, raw: true, color: '#00E5C1' },
  ];

  return (
    <section className="relative border-y border-white/[0.04] overflow-hidden" data-testid="live-threat-dashboard">
      <div className="absolute inset-0 bg-gradient-to-r from-[#FF7A3D]/[0.02] via-transparent to-[#00E5C1]/[0.02]" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5">
        <div className="flex items-center gap-3 mb-4 md:mb-0 md:absolute md:left-6 md:top-1/2 md:-translate-y-1/2 lg:left-12">
          <div className="relative flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full bg-[#FF7A3D] ${pulse ? 'opacity-100' : 'opacity-40'}`} style={{ transition: 'opacity 0.8s ease' }} />
            <span className="font-mono text-[10px] text-[#FF7A3D] tracking-[0.25em] uppercase font-bold">LIVE</span>
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6 md:pl-20 lg:pl-24">
          {items.map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="text-center group"
            >
              <item.icon className="w-4 h-4 mx-auto mb-1.5 opacity-50 group-hover:opacity-90" style={{ color: item.color, transition: 'opacity 0.3s' }} />
              <p className="font-heading text-lg md:text-xl text-white leading-none">
                {item.raw ? item.value : <AnimatedCounter value={item.value} />}
              </p>
              <p className="font-mono text-[8px] md:text-[9px] text-[#888] tracking-[0.15em] mt-1">{item.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
